import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { i18n as I18nType } from "i18next";
import { formatRelativeTime } from "./format-relative-time";
import { resolveNotificationCopy } from "./notification-translation";
import type { NotificationViewItem } from "./types";
import type { ResolvedNotificationsResult } from "./useResolvedNotifications";

export interface NotificationCenterProps {
  userId?: string;
  children?: ReactNode;
}

export interface NotificationCenterContextValue {
  totalCount: number;
  isFetching: boolean;
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
  label: string;
}

export interface NotificationCenterController {
  contextValue: NotificationCenterContextValue;
  isOpen: boolean;
  closeDrawer: () => void;
  title: string;
  emptyTitle: string;
  emptySubtitle: string;
  retryLabel: string;
  items: NotificationViewItem[];
  isLoading: boolean;
  error: Error | null;
  dismissLabel: string;
  categoryLabels: {
    action: string;
    info: string;
  };
  isDeleting: boolean;
  deletingId: string | undefined;
  isUpdatingNotification: (id: string) => boolean;
  refetch: () => Promise<unknown>;
  handleDelete: (notification: NotificationViewItem) => Promise<void>;
  handleNavigate: (notification: NotificationViewItem) => Promise<void>;
  getTimestampLabel: (createdAt: string) => string;
  resolveCopy: (
    descriptor: NotificationViewItem["view"]["title"] | undefined,
    fallback?: string,
  ) => string;
}

interface UseNotificationCenterControllerOptions {
  notifications: ResolvedNotificationsResult;
  i18n: I18nType;
  translate: (key: string, options?: Record<string, unknown>) => string;
  onNavigate: (notification: NotificationViewItem) => Promise<void>;
  onDelete: (notification: NotificationViewItem) => Promise<void>;
  shouldIgnoreDeleteError?: (error: unknown) => boolean;
}

export const useNotificationCenterController = ({
  notifications,
  i18n,
  translate,
  onNavigate,
  onDelete,
  shouldIgnoreDeleteError,
}: UseNotificationCenterControllerOptions): NotificationCenterController => {
  const formatter = useMemo(
    () =>
      new Intl.RelativeTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
        numeric: "auto",
      }),
    [i18n.language, i18n.resolvedLanguage],
  );
  const [isOpen, setIsOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(() => new Set());

  const openDrawer = useCallback(() => setIsOpen(true), []);
  const closeDrawer = useCallback(() => setIsOpen(false), []);
  const toggleDrawer = useCallback(() => {
    setIsOpen((previous) => !previous);
  }, []);

  const markPending = useCallback((id: string) => {
    setPendingIds((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });
  }, []);

  const clearPending = useCallback((id: string) => {
    setPendingIds((current) => {
      if (!current.has(id)) {
        return current;
      }

      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }, []);

  const handleDelete = useCallback(
    async (notification: NotificationViewItem) => {
      if (pendingIds.has(notification.id)) {
        return;
      }

      markPending(notification.id);

      try {
        await onDelete(notification);
      } catch (error) {
        if (!shouldIgnoreDeleteError?.(error)) {
          throw error;
        }
      } finally {
        clearPending(notification.id);
      }
    },
    [clearPending, markPending, onDelete, pendingIds, shouldIgnoreDeleteError],
  );

  const handleNavigate = useCallback(
    async (notification: NotificationViewItem) => {
      if (pendingIds.has(notification.id)) {
        return;
      }

      if (notification.category !== "action") {
        await handleDelete(notification);
        return;
      }

      markPending(notification.id);

      try {
        await onNavigate(notification);
        closeDrawer();
      } finally {
        clearPending(notification.id);
      }
    },
    [clearPending, closeDrawer, handleDelete, markPending, onNavigate, pendingIds],
  );

  const label = translate("topbar.notifications.openLabel");
  const categoryLabels = useMemo(
    () => ({
      action: translate("categories.action", { ns: "notifications" }),
      info: translate("categories.info", { ns: "notifications" }),
    }),
    [translate],
  );

  const resolveCopy = useCallback(
    (
      descriptor: NotificationViewItem["view"]["title"] | undefined,
      fallback = "",
    ) => {
      if (!descriptor) {
        return fallback;
      }

      return resolveNotificationCopy(i18n, descriptor, fallback);
    },
    [i18n],
  );

  const contextValue = useMemo(
    () => ({
      totalCount: notifications.totalCount,
      isFetching: notifications.isFetching,
      isOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      label,
    }),
    [
      closeDrawer,
      isOpen,
      label,
      notifications.isFetching,
      notifications.totalCount,
      openDrawer,
      toggleDrawer,
    ],
  );

  return {
    contextValue,
    isOpen,
    closeDrawer,
    title: translate("topbar.notifications.title"),
    emptyTitle: translate("topbar.notifications.emptyTitle"),
    emptySubtitle: translate("topbar.notifications.emptySubtitle"),
    retryLabel: translate("topbar.notifications.retry"),
    items: notifications.items,
    isLoading: notifications.isLoading,
    error: notifications.error,
    dismissLabel: translate("dismiss", { ns: "notifications" }),
    categoryLabels,
    isDeleting: notifications.isDeleting,
    deletingId: notifications.deletingId,
    isUpdatingNotification: (id: string) =>
      pendingIds.has(id) ||
      (notifications.isDeleting && notifications.deletingId === id),
    refetch: notifications.refetch,
    handleDelete,
    handleNavigate,
    getTimestampLabel: (createdAt: string) =>
      formatRelativeTime(createdAt, formatter),
    resolveCopy,
  };
};
